/**
 * ANANT SAFETY QR — User dashboard for creating & managing emergency QR codes.
 * Tabs: My QRs (create/manage), Alerts inbox, Plans, Downloads.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Shield, QrCode, Bell, Crown, Plus, Trash2, Download, Copy, MapPin, X, Sparkles,
  AlertTriangle, CheckCircle2, ChevronRight,
} from 'lucide-react';
import api from '../../utils/api';
import ATWalletBadge from '../../components/ATWalletBadge';
import ShareButton from '../../components/ShareButton';

export default function AnantSafety() {
  const [tab, setTab] = useState('qrs');
  const [quota, setQuota] = useState(null);
  const [config, setConfig] = useState({ plans: [], asset_types: [] });

  const refresh = useCallback(async () => {
    try {
      const [me, cfg] = await Promise.all([
        api.get('/safety-qr/me'),
        api.get('/safety-qr/config'),
      ]);
      setQuota(me.data);
      setConfig(cfg.data);
    } catch {}
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen pt-20 pb-32 px-4 bg-gradient-to-br from-slate-950 via-red-950/40 to-slate-950 text-white" data-testid="safety-root">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <div className="text-red-400 text-xs font-bold uppercase tracking-widest flex items-center gap-1.5">
              <Shield className="w-4 h-4" /> Anant Safety QR System
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-red-300 via-orange-300 to-amber-300 bg-clip-text text-transparent mt-1">
              Emergency QR · Har Cheez Ki Suraksha
            </h1>
            <p className="text-slate-400 text-sm mt-1">Vehicle · Medical · Luggage · Pet · Personal — scan pe alert.</p>
          </div>
          <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            <ShareButton type="page" itemName="Anant Safety QR" variant="icon" className="scale-110" />
            <a
              href={(() => {
                const link = `${window.location.origin}/anant-safety`;
                const msg = `🛡️ Anant Safety QR try karo!\n\nEmergency me QR scan se aapke family/hospital ko instant alert milta hai.\nMedical · Vehicle · Luggage · Pet · Personal safety — sab kuch.\n\n${link}`;
                return `https://wa.me/?text=${encodeURIComponent(msg)}`;
              })()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:scale-105 transition shadow-lg shadow-green-500/30"
              data-testid="safety-viral-share-wa"
              title="Share on WhatsApp"
            >
              <Sparkles className="w-3.5 h-3.5" /> Share & Earn
            </a>
          </div>
          <ATWalletBadge compact />
          {quota && (() => {
            let ct = '';
            if (quota.active && quota.expires_at) {
              const diff = Math.max(0, new Date(quota.expires_at).getTime() - Date.now());
              const d = Math.floor(diff / 86400000);
              const h = Math.floor((diff % 86400000) / 3600000);
              ct = `${d}d ${h}h left`;
            }
            return (
              <div className={`rounded-xl border px-3 py-2 text-xs ${quota.active ? 'border-emerald-400/40 bg-emerald-500/10' : 'border-red-400/40 bg-red-500/10'}`} data-testid="safety-quota-strip">
                <div className="font-bold">{quota.plan_name || 'No active plan'}</div>
                {quota.active && (quota.has_qr
                  ? <div className="text-slate-300 mt-0.5">1 QR active · <span className="font-mono text-emerald-300">{ct}</span></div>
                  : <div className="text-emerald-300 mt-0.5">Ready to create your QR · <span className="font-mono">{ct}</span></div>)}
                {!quota.active && <div className="text-red-300 mt-0.5">Purchase a plan to create your QR</div>}
              </div>
            );
          })()}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1.5 overflow-x-auto mb-4 pb-1">
          {[{id:'qrs',label:'My QRs',icon:QrCode},{id:'alerts',label:'Alerts',icon:Bell},{id:'plans',label:'Plans',icon:Crown}].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`safety-tab-${t.id}`} className={`shrink-0 px-3 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 transition ${tab === t.id ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-lg' : 'bg-white/5 text-slate-300'}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {tab === 'qrs' && <MyQRsTab quota={quota} config={config} onChange={refresh} />}
        {tab === 'alerts' && <AlertsTab />}
        {tab === 'plans' && <PlansTab plans={config.plans} quota={quota} onPurchased={refresh} />}
      </div>
    </div>
  );
}

// ============ My QRs Tab ============
const MyQRsTab = ({ quota, config, onChange }) => {
  const [qrs, setQrs] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api.get('/safety-qr/qr/mine'); setQrs(r.data.qrs || []); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="safety-qrs-tab">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-400">Aapka QR: <b className="text-white">{qrs.length > 0 ? '1 active' : 'Not created yet'}</b></div>
        <button
          onClick={() => quota?.active && quota.can_create ? setShowCreate(true) : toast.error(quota?.active ? 'Aapka QR pehle se hai — edit karein ya delete karke naya banayein' : 'Purchase a Safety QR plan first.')}
          disabled={!quota?.active || !quota?.can_create}
          data-testid="safety-create-btn"
          className="px-3 py-2 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold text-sm flex items-center gap-1 disabled:opacity-40"
        >
          <Plus className="w-4 h-4" /> Create My QR
        </button>
      </div>
      {qrs.length === 0 && (
        <div className="rounded-xl border border-dashed border-white/10 p-8 text-center text-slate-400 text-sm">
          Abhi tak koi QR nahi banaya. "Naya QR" par click karein.
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {qrs.map((q) => {
          const at = (config.asset_types || []).find((a) => a.id === q.asset_type);
          return (
            <div key={q.qr_id} className="rounded-xl border border-white/10 bg-slate-900/60 p-3" data-testid={`qr-card-${q.qr_id}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="text-2xl">{at?.icon || '🔒'}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{q.asset_label}</div>
                  <div className="text-[11px] text-slate-400">{at?.label || q.asset_type} · {q.scan_count} scans · {q.alert_count} alerts</div>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setSelected(q)} data-testid={`qr-view-${q.qr_id}`} className="flex-1 py-1.5 rounded-lg bg-white/5 text-xs font-bold">View · Download</button>
                <button onClick={() => { setEditing(q); }} data-testid={`qr-edit-${q.qr_id}`} className="p-1.5 rounded-lg bg-fuchsia-500/20 text-fuchsia-300"><Sparkles className="w-3.5 h-3.5" /></button>
                <button onClick={async () => { if (window.confirm('Revoke this QR? It will stop working.')) { await api.delete(`/safety-qr/qr/${q.qr_id}`); toast.success('Revoked'); load(); onChange(); } }} data-testid={`qr-del-${q.qr_id}`} className="p-1.5 rounded-lg bg-red-500/20 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          );
        })}
      </div>
      {showCreate && <QRCreateModal config={config} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); onChange(); }} />}
      {selected && <QRViewModal qr={selected} config={config} onClose={() => setSelected(null)} />}
      {editing && <QREditModal qr={editing} config={config} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};

const QREditModal = ({ qr, config, onClose, onSaved }) => {
  const [form, setForm] = useState({
    asset_type: qr.asset_type, asset_label: qr.asset_label || '', contact_name: qr.contact_name || '',
    contact_mobile: qr.contact_mobile || '', whatsapp: qr.whatsapp || '',
    medical_info: qr.medical_info || '', custom_message: qr.custom_message || '',
    show_medical: !!qr.show_medical,
    emergency_headline: qr.emergency_headline || (config.default_emergency_headline || '⚠️ EMERGENCY? SCAN FOR HELP'),
    additional_info: qr.additional_info || '',
    custom_emergency_numbers: qr.custom_emergency_numbers || (config.default_emergency_numbers || []),
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNum = (i, k, v) => setForm((f) => ({ ...f, custom_emergency_numbers: (f.custom_emergency_numbers || []).map((n, idx) => idx === i ? { ...n, [k]: v } : n) }));
  const addNum = () => setForm((f) => ({ ...f, custom_emergency_numbers: [...(f.custom_emergency_numbers || []), { label: '', number: '', icon: '📞' }] }));
  const delNum = (i) => setForm((f) => ({ ...f, custom_emergency_numbers: (f.custom_emergency_numbers || []).filter((_, idx) => idx !== i) }));
  const submit = async () => {
    setBusy(true);
    try {
      await api.put(`/safety-qr/qr/${qr.qr_id}`, form);
      toast.success('QR updated');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 rounded-2xl border border-white/10 max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="safety-edit-modal">
        <div className="flex justify-between items-center mb-3"><h3 className="text-lg font-bold">Edit Safety QR</h3><button onClick={onClose}><X className="w-4 h-4" /></button></div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <select value={form.asset_type} onChange={(e) => set('asset_type', e.target.value)} className="col-span-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
            {(config.asset_types || []).map((a) => <option key={a.id} value={a.id} className="bg-slate-900">{a.icon} {a.label}</option>)}
          </select>
          <input placeholder="Asset label *" value={form.asset_label} onChange={(e) => set('asset_label', e.target.value)} className="col-span-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          <div className="col-span-2">
            <label className="text-[10px] uppercase font-bold text-amber-300">Emergency Headline (QR ke upper eye-catchy message)</label>
            <input value={form.emergency_headline} onChange={(e) => set('emergency_headline', e.target.value)} data-testid="edit-emerg-headline" className="w-full bg-amber-500/10 border border-amber-400/30 rounded-lg px-3 py-2 font-bold" />
          </div>
          <input placeholder="Contact Name *" value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          <input placeholder="Mobile *" value={form.contact_mobile} onChange={(e) => set('contact_mobile', e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          <input placeholder="WhatsApp" value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} className="col-span-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          <textarea placeholder="Medical Info" value={form.medical_info} onChange={(e) => set('medical_info', e.target.value)} className="col-span-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          <label className="col-span-2 flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.show_medical} onChange={(e) => set('show_medical', e.target.checked)} />
            Show medical info publicly
          </label>
          <textarea placeholder="Custom message" value={form.custom_message} onChange={(e) => set('custom_message', e.target.value)} className="col-span-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          <textarea placeholder="Additional info (address, tagline, extra notes)" value={form.additional_info} onChange={(e) => set('additional_info', e.target.value)} data-testid="edit-additional-info" className="col-span-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          {/* Editable emergency numbers */}
          <div className="col-span-2 border-t border-white/10 pt-2">
            <div className="text-[10px] uppercase font-bold text-red-300 mb-1.5">Emergency Numbers (India defaults · editable)</div>
            <div className="space-y-1.5">
              {(form.custom_emergency_numbers || []).map((n, i) => (
                <div key={i} className="flex gap-1.5 items-center">
                  <input value={n.icon || ''} onChange={(e) => setNum(i, 'icon', e.target.value)} className="w-10 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-center" />
                  <input value={n.label || ''} placeholder="Label" onChange={(e) => setNum(i, 'label', e.target.value)} className="flex-1 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5" />
                  <input value={n.number || ''} placeholder="Number" onChange={(e) => setNum(i, 'number', e.target.value)} className="w-24 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5" />
                  <button type="button" onClick={() => delNum(i)} className="p-1.5 rounded-lg bg-red-500/20 text-red-300"><X className="w-3 h-3" /></button>
                </div>
              ))}
              <button type="button" onClick={addNum} className="text-[11px] text-emerald-300 font-bold">+ Add number</button>
            </div>
          </div>
        </div>
        <button onClick={submit} disabled={busy} data-testid="safety-edit-save" className="w-full mt-3 py-2 rounded-lg bg-fuchsia-500 text-white font-bold text-sm disabled:opacity-60">{busy ? 'Saving…' : 'Update QR'}</button>
      </div>
    </div>
  );
};

const QRCreateModal = ({ config, onClose, onCreated }) => {
  const [form, setForm] = useState({
    asset_type: 'vehicle', asset_label: '', contact_name: '', contact_mobile: '',
    whatsapp: '', medical_info: '', custom_message: '', show_medical: false,
    emergency_headline: config.default_emergency_headline || '⚠️ EMERGENCY? SCAN FOR HELP',
    additional_info: '',
    custom_emergency_numbers: (config.default_emergency_numbers || []).map((n) => ({ ...n })),
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNum = (i, k, v) => setForm((f) => ({ ...f, custom_emergency_numbers: (f.custom_emergency_numbers || []).map((n, idx) => idx === i ? { ...n, [k]: v } : n) }));
  const addNum = () => setForm((f) => ({ ...f, custom_emergency_numbers: [...(f.custom_emergency_numbers || []), { label: '', number: '', icon: '📞' }] }));
  const delNum = (i) => setForm((f) => ({ ...f, custom_emergency_numbers: (f.custom_emergency_numbers || []).filter((_, idx) => idx !== i) }));
  const resetDefaults = () => setForm((f) => ({ ...f, custom_emergency_numbers: (config.default_emergency_numbers || []).map((n) => ({ ...n })) }));
  const submit = async () => {
    if (!form.asset_label || !form.contact_name || !form.contact_mobile) return toast.error('Zaroori fields bharein');
    setBusy(true);
    try {
      await api.post('/safety-qr/qr/create', form);
      toast.success('✅ QR ban gaya! Ab print karke lagayein.');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 rounded-2xl border border-white/10 max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="safety-create-modal">
        <div className="flex justify-between items-center mb-3"><h3 className="text-lg font-bold">Naya Safety QR</h3><button onClick={onClose}><X className="w-4 h-4" /></button></div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <select value={form.asset_type} onChange={(e) => set('asset_type', e.target.value)} data-testid="safety-form-type" className="col-span-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
            {(config.asset_types || []).map((a) => <option key={a.id} value={a.id} className="bg-slate-900">{a.icon} {a.label} — {a.hint}</option>)}
          </select>
          <input placeholder="Asset label (e.g. White Swift MH31-1234) *" value={form.asset_label} onChange={(e) => set('asset_label', e.target.value)} data-testid="safety-form-label" className="col-span-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          <div className="col-span-2">
            <label className="text-[10px] uppercase font-bold text-amber-300">Emergency Headline (editable)</label>
            <input value={form.emergency_headline} onChange={(e) => set('emergency_headline', e.target.value)} data-testid="safety-form-headline" className="w-full bg-amber-500/10 border border-amber-400/30 rounded-lg px-3 py-2 font-bold" />
          </div>
          <input placeholder="Contact Name *" value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          <input placeholder="Contact Mobile *" value={form.contact_mobile} onChange={(e) => set('contact_mobile', e.target.value)} data-testid="safety-form-mobile" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          <input placeholder="WhatsApp (agar alag)" value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} className="col-span-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          <textarea placeholder="Medical Info (Blood group, allergies) — optional" value={form.medical_info} onChange={(e) => set('medical_info', e.target.value)} className="col-span-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          <label className="col-span-2 flex items-center gap-2 text-xs text-slate-300">
            <input type="checkbox" checked={form.show_medical} onChange={(e) => set('show_medical', e.target.checked)} data-testid="safety-form-med-toggle" />
            Medical info scanner ko dikhayein (Emergency ke liye recommended)
          </label>
          <textarea placeholder="Custom message on QR page (e.g. 'Please call if you see me hit')" value={form.custom_message} onChange={(e) => set('custom_message', e.target.value)} className="col-span-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          <textarea placeholder="Additional info (address, tagline, extra notes)" value={form.additional_info} onChange={(e) => set('additional_info', e.target.value)} data-testid="safety-form-additional" className="col-span-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          {/* Editable emergency numbers (defaults pre-filled) */}
          <div className="col-span-2 border-t border-white/10 pt-2" data-testid="safety-form-emerg-numbers">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] uppercase font-bold text-red-300">Emergency Numbers (India defaults · editable)</div>
              <button type="button" onClick={resetDefaults} className="text-[10px] text-slate-400 hover:text-emerald-300 font-bold">↺ Reset defaults</button>
            </div>
            <div className="space-y-1.5">
              {(form.custom_emergency_numbers || []).map((n, i) => (
                <div key={i} className="flex gap-1.5 items-center">
                  <input value={n.icon || ''} onChange={(e) => setNum(i, 'icon', e.target.value)} className="w-10 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-center" />
                  <input value={n.label || ''} placeholder="Label" onChange={(e) => setNum(i, 'label', e.target.value)} className="flex-1 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5" />
                  <input value={n.number || ''} placeholder="Number" onChange={(e) => setNum(i, 'number', e.target.value)} className="w-24 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5" />
                  <button type="button" onClick={() => delNum(i)} className="p-1.5 rounded-lg bg-red-500/20 text-red-300"><X className="w-3 h-3" /></button>
                </div>
              ))}
              <button type="button" onClick={addNum} data-testid="safety-form-add-num" className="text-[11px] text-emerald-300 font-bold">+ Add number</button>
            </div>
          </div>
        </div>
        <button onClick={submit} disabled={busy} data-testid="safety-create-save" className="w-full mt-3 py-2 rounded-lg bg-red-500 text-white font-bold text-sm disabled:opacity-60">{busy ? 'Creating…' : 'Create QR'}</button>
      </div>
    </div>
  );
};

const QRViewModal = ({ qr, config, onClose }) => {
  const publicUrl = `${window.location.origin}/safety-qr/scan/${qr.qr_id}`;
  const [size, setSize] = useState('medium');
  const [printers, setPrinters] = useState([]);
  const [theme, setTheme] = useState('classic');
  const [customFg, setCustomFg] = useState('#dc2626');
  const [customBg, setCustomBg] = useState('#ffffff');
  useEffect(() => {
    api.get('/safety-qr/printers').then((r) => setPrinters(r.data.printers || [])).catch(() => {});
  }, []);
  const sizes = {
    small:  { px: 200, label: 'Small (Sticker)',  desc: 'Vehicle sticker, wallet card, keychain' },
    medium: { px: 400, label: 'Medium (Poster)',  desc: 'Wall poster, notice board, shop counter' },
    large:  { px: 800, label: 'Large (Metal)',    desc: 'Metal plate, hoarding, printed signage' },
  };
  // Color themes — hi-contrast presets. All use ecc=H (30% error correction) for reliable scanning even with color.
  const themes = {
    classic:    { fg: '000000', bg: 'ffffff', label: 'Classic',    swatch: 'bg-slate-900' },
    fire:       { fg: 'dc2626', bg: 'fff7ed', label: 'Fire Red',   swatch: 'bg-red-600' },
    royal:      { fg: '1e40af', bg: 'eff6ff', label: 'Royal Blue', swatch: 'bg-blue-700' },
    emerald:    { fg: '047857', bg: 'ecfdf5', label: 'Emerald',    swatch: 'bg-emerald-700' },
    neon:       { fg: '7c3aed', bg: 'faf5ff', label: 'Neon Purple',swatch: 'bg-violet-600' },
    gold:       { fg: 'a16207', bg: 'fefce8', label: 'Gold',       swatch: 'bg-yellow-600' },
    midnight:   { fg: 'e2e8f0', bg: '0f172a', label: 'Midnight',   swatch: 'bg-slate-950' },
    custom:     { fg: customFg.replace('#',''), bg: customBg.replace('#',''), label: 'Custom', swatch: 'bg-gradient-to-r from-fuchsia-500 to-cyan-500' },
  };
  const activeTheme = themes[theme];
  const qrImg = `https://api.qrserver.com/v1/create-qr-code/?size=${sizes[size].px}x${sizes[size].px}&data=${encodeURIComponent(publicUrl)}&color=${activeTheme.fg}&bgcolor=${activeTheme.bg}&ecc=H&qzone=2&format=png`;
  const downloadImg = async () => {
    try {
      // Load raw QR image
      const resp = await fetch(qrImg);
      const blob = await resp.blob();
      const qrBitmap = await new Promise((res, rej) => {
        const img = new Image(); img.crossOrigin = 'anonymous';
        img.onload = () => res(img); img.onerror = rej;
        img.src = URL.createObjectURL(blob);
      });
      // Compose emergency-styled poster on canvas
      const px = sizes[size].px;
      const canvasW = px + 80;
      const headerH = Math.max(80, Math.floor(px * 0.18));
      const numsH = Math.max(90, Math.floor(px * 0.22));
      const infoH = qr.additional_info ? Math.max(60, Math.floor(px * 0.14)) : 0;
      const footerH = 40;
      const canvasH = headerH + px + 20 + numsH + infoH + footerH;
      const canvas = document.createElement('canvas');
      canvas.width = canvasW; canvas.height = canvasH;
      const ctx = canvas.getContext('2d');
      // Background — match theme
      ctx.fillStyle = `#${activeTheme.bg}`;
      ctx.fillRect(0, 0, canvasW, canvasH);
      // Header — headline strip
      ctx.fillStyle = `#${activeTheme.fg}`;
      ctx.fillRect(0, 0, canvasW, headerH);
      ctx.fillStyle = `#${activeTheme.bg}`;
      ctx.font = `bold ${Math.floor(headerH * 0.35)}px Arial, sans-serif`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      const headline = (qr.emergency_headline || '⚠️ EMERGENCY? SCAN FOR HELP').slice(0, 40);
      ctx.fillText(headline, canvasW / 2, headerH / 2 - 8);
      ctx.font = `${Math.floor(headerH * 0.22)}px Arial, sans-serif`;
      ctx.fillText(`${(qr.asset_type || '').toUpperCase()} · ${qr.asset_label || ''}`, canvasW / 2, headerH / 2 + Math.floor(headerH * 0.22));
      // QR image centered
      ctx.drawImage(qrBitmap, (canvasW - px) / 2, headerH + 10, px, px);
      // Emergency numbers list
      const nums = (qr.custom_emergency_numbers && qr.custom_emergency_numbers.length ? qr.custom_emergency_numbers : (config.default_emergency_numbers || [])).slice(0, 6);
      ctx.fillStyle = `#${activeTheme.fg}`;
      ctx.font = `bold ${Math.floor(numsH * 0.14)}px Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText('📞 EMERGENCY NUMBERS', canvasW / 2, headerH + px + 30);
      ctx.font = `bold ${Math.floor(numsH * 0.16)}px Arial, sans-serif`;
      const colY = headerH + px + 55;
      const rows = 2, cols = 3;
      nums.forEach((n, i) => {
        const r = Math.floor(i / cols), c = i % cols;
        const x = (canvasW / cols) * c + canvasW / (cols * 2);
        const y = colY + r * (numsH * 0.35);
        ctx.fillText(`${n.icon || '📞'} ${n.label}: ${n.number}`, x, y);
      });
      // Additional info block
      if (qr.additional_info) {
        const infoY = headerH + px + 20 + numsH + 20;
        ctx.font = `${Math.floor(infoH * 0.22)}px Arial, sans-serif`;
        ctx.textAlign = 'center';
        // Word-wrap simple
        const words = qr.additional_info.split(' ');
        let line = '', y = infoY;
        const maxW = canvasW - 40;
        words.forEach((w) => {
          const test = line + w + ' ';
          if (ctx.measureText(test).width > maxW) { ctx.fillText(line, canvasW / 2, y); line = w + ' '; y += infoH * 0.3; }
          else line = test;
        });
        if (line) ctx.fillText(line, canvasW / 2, y);
      }
      // Footer branding
      ctx.font = `${Math.floor(footerH * 0.32)}px Arial, sans-serif`;
      ctx.fillStyle = `#${activeTheme.fg}`;
      ctx.globalAlpha = 0.7;
      ctx.fillText(`Anant Web Portal — Safety QR · ${qr.qr_id}`, canvasW / 2, canvasH - footerH / 2);
      ctx.globalAlpha = 1;
      // Download
      canvas.toBlob((finalBlob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(finalBlob);
        a.download = `anant-safety-${qr.qr_id}-${size}-${theme}.png`;
        a.click();
        toast.success(`Safety poster downloaded (${sizes[size].label} · ${activeTheme.label})`);
      }, 'image/png');
    } catch (e) {
      toast.error('Download failed. Try again.');
    }
  };
  const printPoster = () => {
    const nums = (qr.custom_emergency_numbers && qr.custom_emergency_numbers.length ? qr.custom_emergency_numbers : (config?.default_emergency_numbers || [])).slice(0, 6);
    const w = window.open('', '_blank', 'width=800,height=1000');
    if (!w) return toast.error('Popup blocked. Allow popups and retry.');
    const numsHtml = nums.map((n) => `<div style="font-size:18px;font-weight:800;color:#dc2626;margin:6px 0">${n.icon || '📞'} ${n.label}: ${n.number}</div>`).join('');
    w.document.write(`<!DOCTYPE html><html><head><title>Safety QR — ${qr.asset_label}</title>
      <style>
        @page { size: A4; margin: 15mm; }
        body { font-family: Arial, sans-serif; text-align: center; margin: 0; padding: 20px; background: #${activeTheme.bg}; color: #${activeTheme.fg}; }
        .headline { background: #${activeTheme.fg}; color: #${activeTheme.bg}; padding: 18px; font-size: 28px; font-weight: 900; border-radius: 12px; margin-bottom: 20px; }
        .asset { font-size: 20px; font-weight: 800; margin-bottom: 12px; }
        .qr-wrap { background: #${activeTheme.bg}; padding: 12px; border-radius: 12px; display: inline-block; border: 3px solid #${activeTheme.fg}; }
        .qr-wrap img { display: block; }
        .nums { margin-top: 24px; padding: 16px; background: rgba(255,255,255,0.6); border-radius: 12px; }
        .nums h3 { color: #${activeTheme.fg}; margin: 0 0 10px; }
        .info { margin-top: 16px; padding: 12px; background: rgba(255,255,255,0.5); border-radius: 10px; font-size: 14px; }
        .footer { margin-top: 24px; font-size: 11px; color: #666; }
        @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      </style></head><body>
      <div class="headline">${qr.emergency_headline || '⚠️ EMERGENCY? SCAN FOR HELP'}</div>
      <div class="asset">${(qr.asset_type || '').toUpperCase()} · ${qr.asset_label || ''}</div>
      <div class="qr-wrap"><img src="${qrImg}" width="360" height="360" alt="QR"/></div>
      <div class="nums"><h3>📞 EMERGENCY NUMBERS</h3>${numsHtml}</div>
      ${qr.additional_info ? `<div class="info"><b>Info:</b> ${qr.additional_info}</div>` : ''}
      <div class="footer">Anant Web Portal · Safety QR ID: ${qr.qr_id}</div>
      <script>window.onload = () => { setTimeout(() => window.print(), 500); };</script>
      </body></html>`);
    w.document.close();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 rounded-2xl border border-white/10 max-w-md w-full p-5 text-center max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="safety-view-modal">
        <div className="flex justify-between items-center mb-3"><h3 className="text-lg font-bold truncate">{qr.asset_label}</h3><button onClick={onClose}><X className="w-4 h-4" /></button></div>
        {qr.emergency_headline && (
          <div className="mb-3 rounded-xl bg-gradient-to-r from-red-600 to-orange-600 px-3 py-2 text-white font-extrabold text-sm shadow-lg" data-testid="view-emerg-headline">
            {qr.emergency_headline}
          </div>
        )}
        <div className="rounded-xl p-4 inline-block shadow-2xl" style={{ backgroundColor: `#${activeTheme.bg}` }}>
          <img src={qrImg} alt="Safety QR" data-testid="safety-qr-img" style={{ width: 240, height: 240 }} />
        </div>
        <div className="text-[10px] text-slate-400 mt-1">{sizes[size].px}×{sizes[size].px}px · {activeTheme.label} · ECC-H</div>

        {/* Primary action buttons — moved up for mobile visibility */}
        <div className="mt-3 grid grid-cols-3 gap-1.5" data-testid="qr-actions-top">
          <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('Link copied'); }} data-testid="safety-copy-link" className="py-2 rounded-lg bg-white/10 text-xs font-bold flex items-center justify-center gap-1"><Copy className="w-3 h-3" /> Copy Link</button>
          <button onClick={downloadImg} data-testid="safety-download" className="py-2 rounded-lg bg-red-500 text-white text-xs font-bold flex items-center justify-center gap-1"><Download className="w-3 h-3" /> Download</button>
          <button onClick={printPoster} data-testid="safety-print" className="py-2 rounded-lg bg-fuchsia-500 text-white text-xs font-bold">🖨️ Print</button>
        </div>

        {qr.additional_info && (
          <div className="mt-3 rounded-lg bg-white/5 border border-white/10 p-2 text-[11px] text-slate-300 text-left" data-testid="view-additional-info">
            <b className="text-slate-100">Additional Info:</b> {qr.additional_info}
          </div>
        )}

        {/* Color Theme selector — Lucrative, high-quality colored QRs */}
        <div className="mt-3 border-t border-white/10 pt-3">
          <div className="text-xs font-bold text-slate-300 mb-2 flex items-center gap-1.5">🎨 Colour Theme <span className="text-[9px] text-slate-500 font-normal">(scan reliability guaranteed at ECC-H)</span></div>
          <div className="grid grid-cols-4 gap-1.5" data-testid="qr-theme-grid">
            {Object.entries(themes).map(([key, t]) => (
              <button
                key={key}
                onClick={() => setTheme(key)}
                data-testid={`qr-theme-${key}`}
                className={`p-1.5 rounded-lg text-[10px] font-bold transition ${theme === key ? 'ring-2 ring-red-300 bg-white/10' : 'bg-white/5 hover:bg-white/10'}`}
              >
                <div className={`h-6 rounded ${t.swatch} mb-0.5 border border-white/20`} />
                {t.label}
              </button>
            ))}
          </div>
          {theme === 'custom' && (
            <div className="mt-2 flex gap-2 items-center justify-center text-[10px]" data-testid="qr-theme-custom-pickers">
              <label className="flex items-center gap-1">FG <input type="color" value={customFg} onChange={(e) => setCustomFg(e.target.value)} className="w-8 h-6 rounded cursor-pointer" /></label>
              <label className="flex items-center gap-1">BG <input type="color" value={customBg} onChange={(e) => setCustomBg(e.target.value)} className="w-8 h-6 rounded cursor-pointer" /></label>
            </div>
          )}
        </div>

        {/* Size selector */}
        <div className="mt-3 border-t border-white/10 pt-3">
          <div className="text-xs font-bold text-slate-300 mb-2">Download Size chunein:</div>
          <div className="grid grid-cols-3 gap-1.5">
            {Object.entries(sizes).map(([key, s]) => (
              <button
                key={key}
                onClick={() => setSize(key)}
                data-testid={`safety-size-${key}`}
                className={`px-2 py-2 rounded-lg text-[11px] font-bold transition ${size === key ? 'bg-red-500 text-white ring-2 ring-red-300' : 'bg-white/5 text-slate-300'}`}
              >
                <div className="text-[10px]">{s.label}</div>
                <div className="text-[9px] opacity-70 font-normal mt-0.5">{s.px}px</div>
              </button>
            ))}
          </div>
          <div className="text-[10px] text-slate-400 mt-1.5 italic">{sizes[size].desc}</div>
        </div>

        <div className="text-xs text-slate-400 mt-3 break-all">{publicUrl}</div>
        {/* Suggested Printers */}
        {printers.length > 0 && (
          <div className="mt-4 border-t border-white/10 pt-3 text-left" data-testid="suggested-printers">
            <div className="text-xs font-bold text-amber-300 mb-2">🖨️ Suggested Printers — Print karwane ke liye contact karein:</div>
            <div className="space-y-1.5">
              {printers.slice(0, 3).map((p) => {
                const num = (p.whatsapp || p.mobile || '').replace(/\D/g, '');
                const waNum = num.length === 10 ? '91' + num : num;
                const msg = `Namaste 🙏 Mujhe apna Anant Safety QR print karwana hai.\nSize: ${sizes[size].label} (${sizes[size].px}px)\nUse: ${sizes[size].desc}\nKya rate hoga?`;
                return (
                  <div key={p.id} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2" data-testid={`printer-${p.id}`}>
                    <div className="min-w-0">
                      <div className="text-xs font-bold truncate">{p.name}{p.city ? <span className="text-slate-400 font-normal"> · {p.city}</span> : null}</div>
                      {p.note && <div className="text-[10px] text-slate-400 truncate">{p.note}</div>}
                    </div>
                    <a href={`https://wa.me/${waNum}?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer"
                      onClick={() => { api.post('/safety-qr/printers/orders/report', { printer_id: p.id, qr_id: qr.qr_id, size: sizes[size].label }).catch(() => {}); }}
                      data-testid={`printer-wa-${p.id}`}
                      className="shrink-0 ml-2 px-2.5 py-1.5 rounded-lg bg-emerald-500 text-white text-[10px] font-bold">
                      WhatsApp
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-4 text-[11px] text-slate-500 border-t border-white/10 pt-3">
          Total Scans: <b className="text-white">{qr.scan_count}</b> · Alerts Received: <b className="text-white">{qr.alert_count}</b>
        </div>
      </div>
    </div>
  );
};

// ============ Alerts inbox with repeating alarm ============
// Web Audio API — free, no external tone URL. Plays a 3-beep siren burst every 2 min
// while any unread alert exists, until user marks all as read or leaves the tab.
const playAlarmBeep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;
    // 3-beep siren pattern
    [0, 0.35, 0.7].forEach((offset) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(880, now + offset);
      osc.frequency.linearRampToValueAtTime(440, now + offset + 0.25);
      gain.gain.setValueAtTime(0.001, now + offset);
      gain.gain.exponentialRampToValueAtTime(0.35, now + offset + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.3);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(now + offset); osc.stop(now + offset + 0.32);
    });
    setTimeout(() => { try { ctx.close(); } catch {} }, 1500);
  } catch (e) { /* audio not supported */ }
};

const AlertsTab = () => {
  const [alerts, setAlerts] = useState([]);
  const [community, setCommunity] = useState([]);
  const [alarmOn, setAlarmOn] = useState(true);
  const load = useCallback(async () => {
    try { const r = await api.get('/safety-qr/alerts/mine'); setAlerts(r.data.alerts || []); } catch {}
    try { const r2 = await api.get('/safety-qr/community-alerts'); setCommunity(r2.data.alerts || []); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);
  const unreadCount = alerts.filter((a) => a.status === 'unread').length;

  // Auto-refresh + alarm loop: every 2 min if any unread → beep + refetch
  useEffect(() => {
    if (!alarmOn) return undefined;
    // Beep once immediately when unread count changes to >0
    if (unreadCount > 0) playAlarmBeep();
    const t = setInterval(async () => {
      try {
        const r = await api.get('/safety-qr/alerts/mine');
        const items = r.data.alerts || [];
        const stillUnread = items.filter((a) => a.status === 'unread').length;
        setAlerts(items);
        if (stillUnread > 0) playAlarmBeep();
      } catch {}
    }, 120000); // every 2 minutes
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadCount > 0, alarmOn]);

  const markRead = async (id) => { await api.post(`/safety-qr/alerts/${id}/read`); load(); };
  const markAllRead = async () => {
    await Promise.all(alerts.filter((a) => a.status === 'unread').map((a) => api.post(`/safety-qr/alerts/${a.id}/read`)));
    toast.success('Sabhi alerts read mark ho gaye');
    load();
  };
  return (
    <div className="space-y-2" data-testid="safety-alerts-tab">
      {unreadCount > 0 && (
        <div className="rounded-xl border border-red-400/50 bg-red-500/15 p-3 flex items-center justify-between gap-2 animate-pulse" data-testid="safety-alarm-strip">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-300" />
            <div>
              <div className="font-bold text-sm text-red-100">🚨 {unreadCount} unread alert(s)</div>
              <div className="text-[11px] text-red-200/80">Alarm har 2 minute me bajega jab tak sab read na ho</div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setAlarmOn((v) => !v)} data-testid="safety-alarm-toggle" className="px-2.5 py-1.5 rounded-lg bg-white/10 text-[11px] font-bold">
              {alarmOn ? '🔇 Mute' : '🔊 Unmute'}
            </button>
            <button onClick={markAllRead} data-testid="safety-alarm-clear" className="px-2.5 py-1.5 rounded-lg bg-emerald-500 text-white text-[11px] font-bold">Mark All Read</button>
          </div>
        </div>
      )}
      {alerts.length === 0 && <div className="rounded-xl border border-dashed border-white/10 p-8 text-center text-slate-400 text-sm">Koi alert nahi. Aapke QR safe hain 🛡️</div>}
      {alerts.map((a) => (
        <div key={a.id} className={`rounded-xl border p-3 ${a.status === 'unread' ? 'border-red-400/50 bg-red-500/10' : 'border-white/10 bg-slate-900/40'}`} data-testid={`alert-${a.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="font-bold text-sm">{a.asset?.asset_label || 'Unknown asset'}</span>
                {a.status === 'unread' && <span className="text-[10px] bg-red-500 text-white px-1.5 py-0.5 rounded-full">NEW</span>}
              </div>
              <div className="text-sm text-slate-200">{a.message}</div>
              {a.lat && a.lng && (
                <a href={`https://maps.google.com/?q=${a.lat},${a.lng}`} target="_blank" rel="noreferrer" className="text-xs text-blue-300 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> View on Map</a>
              )}
              <div className="text-[10px] text-slate-500 mt-1">{new Date(a.created_at).toLocaleString()} · IP: {a.ip}</div>
            </div>
            {a.status === 'unread' && <button onClick={() => markRead(a.id)} className="text-[10px] text-emerald-300 font-bold">Mark Read</button>}
          </div>
        </div>
      ))}
      {/* Community Panic Chain — 24h nearby alerts from other users */}
      {community.length > 0 && (
        <div className="mt-4 pt-3 border-t border-white/10" data-testid="community-alerts">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-fuchsia-300" />
            <div className="font-bold text-sm">🚨 Community Panic Chain <span className="text-[10px] text-slate-400 font-normal">· Last 24h · {community.length} alerts</span></div>
          </div>
          <div className="text-[11px] text-slate-400 mb-2">Anant Network par doosre users ke alerts — awareness ke liye. Aap safe rahein 🛡️</div>
          <div className="space-y-1.5">
            {community.slice(0, 10).map((c, i) => (
              <div key={i} className="rounded-lg border border-fuchsia-400/20 bg-fuchsia-500/5 p-2" data-testid={`community-alert-${i}`}>
                <div className="text-xs text-fuchsia-200 flex items-center gap-1.5">
                  <span className="text-[10px] uppercase font-bold bg-fuchsia-500/20 px-1.5 py-0.5 rounded">{c.asset_type}</span>
                  <span className="opacity-70">{c.asset_label_masked}</span>
                  {c.lat && c.lng && <a href={`https://maps.google.com/?q=${c.lat},${c.lng}`} target="_blank" rel="noreferrer" className="ml-auto text-blue-300"><MapPin className="w-3 h-3" /></a>}
                </div>
                <div className="text-xs text-slate-300 mt-0.5 truncate">{c.message}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{new Date(c.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============ Plans Tab ============
const PlansTab = ({ plans, quota, onPurchased }) => {
  const [busy, setBusy] = useState(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(t); }, []);
  const buy = async (planId) => {
    if (!window.confirm('AT wallet se plan kharidne wale hain. Confirm?')) return;
    setBusy(planId);
    try {
      const r = await api.post('/safety-qr/plans/purchase', { plan_id: planId });
      toast.success(`✅ Plan active! Ab apna Safety QR banayein · Valid till ${new Date(r.data.subscription.expires_at).toLocaleDateString()}`);
      onPurchased();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(null); }
  };
  // Countdown text
  let countdown = '';
  if (quota?.active && quota?.expires_at) {
    const diff = Math.max(0, new Date(quota.expires_at).getTime() - now);
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    countdown = `${days}d ${hours}h ${mins}m remaining`;
  }
  return (
    <div className="space-y-3" data-testid="safety-plans-tab">
      <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 p-3 text-xs text-amber-100">
        💡 <b>50% Affiliate Network Pool:</b> Har purchase pe 33% amount 7-level webchain mein distribute hota hai. Apne referrals ke plans se kamai karein.
      </div>
      {quota?.active && (
        <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3 text-xs text-emerald-100 flex items-center justify-between gap-2" data-testid="safety-active-plan-strip">
          <div>
            <b>✅ Active Plan:</b> {quota.plan_name} — <span className="font-mono">{countdown}</span>
            {quota.allowed > 1 && <span className="ml-2 text-emerald-300 font-bold" data-testid="qr-remaining">· <span className="text-emerald-200 text-base">{Math.max(0, quota.allowed - quota.used)}</span> / {quota.allowed} QR baaki ({quota.used} used)</span>}
          </div>
          <div className="text-[10px] opacity-70">{quota.can_create ? 'Naye QR bana sakte ho' : 'Slot bhar gaye — expiry ke baad renew karo'}</div>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {plans.map((p) => {
          const isCurrent = quota?.plan_id === p.id;
          const disabled = busy || quota?.active; // block ALL buys when any plan active
          return (
            <div key={p.id} className={`rounded-2xl border p-4 relative ${isCurrent ? 'border-emerald-400/60 bg-emerald-500/10' : 'border-white/10 bg-slate-900/60'}`} data-testid={`safety-plan-${p.id}`}>
              {isCurrent && <div className="absolute -top-2 right-2 text-[10px] bg-emerald-500 text-white px-2 py-0.5 rounded-full font-bold">CURRENT</div>}
              <div className="text-sm font-bold">{p.name}</div>
              <div className="text-3xl font-extrabold mt-2">{p.price_at} <span className="text-xs text-slate-400 font-normal">AT</span></div>
              <div className="text-xs text-slate-400 mt-1">{p.duration_days} days validity</div>
              <div className="text-[11px] text-emerald-300 mt-1">✓ 1 Safety QR (edit anytime)</div>
              <div className="text-[11px] text-emerald-300">✓ 3 print sizes free</div>
              <button onClick={() => buy(p.id)} disabled={disabled} data-testid={`safety-buy-${p.id}`}
                className={`w-full mt-3 py-2 rounded-lg text-white font-bold text-xs ${isCurrent ? 'bg-emerald-600' : 'bg-gradient-to-r from-red-500 to-orange-500'} disabled:opacity-40 disabled:cursor-not-allowed`}>
                {isCurrent ? 'Active' : busy === p.id ? 'Processing…' : quota?.active ? 'Locked (renew after expiry)' : 'Buy Plan'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
